import React from 'react';
import type { HoverPreviewState } from '../../types/table';
import { TABLE_Z_INDEX } from '../../constants/table';

export interface HoverPreviewTooltipProps {
  hoverPreview: HoverPreviewState;
  hoverPreviewRef: React.RefObject<HTMLDivElement | null>;
  isMouseInPreview: React.MutableRefObject<boolean>;
  onClose: () => void;
}

/**
 * Modern minimal hover preview tooltip for truncated cells and headers
 */
export const HoverPreviewTooltip: React.FC<HoverPreviewTooltipProps> = ({
  hoverPreview,
  hoverPreviewRef,
  isMouseInPreview,
  onClose,
}) => {
  if (!hoverPreview.show) return null;

  return (
    <div
      ref={hoverPreviewRef as React.RefObject<HTMLDivElement>}
      className="fixed bg-white rounded-lg overflow-y-auto overflow-x-hidden pointer-events-auto"
      style={{
        left: `${hoverPreview.x}px`,
        top: `${hoverPreview.y}px`,
        maxWidth: `${hoverPreview.maxWidth}px`,
        maxHeight: '40vh',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)',
        animation: 'tooltipFadeIn 150ms ease-out',
        zIndex: TABLE_Z_INDEX.HOVER_PREVIEW,
      }}
      onMouseEnter={() => {
        isMouseInPreview.current = true;
      }}
      onMouseLeave={() => {
        isMouseInPreview.current = false;
        onClose();
      }}
    >
      <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {hoverPreview.isHeader ? (
        <div className="p-3">
          {/* Header Title */}
          <div
            className={`font-medium text-[13px] text-neutral-800 leading-snug ${
              hoverPreview.dataType || hoverPreview.description ? 'mb-2' : ''
            }`}
          >
            {hoverPreview.title}
          </div>

          {/* Data Type Badge */}
          {hoverPreview.dataType && (
            <div className={hoverPreview.description ? 'mb-2' : ''}>
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-neutral-100 text-neutral-500">
                {hoverPreview.dataType}
              </span>
            </div>
          )}

          {/* Description (if available) */}
          {hoverPreview.description && (
            <div className="text-[12px] text-neutral-500 leading-relaxed">
              {hoverPreview.description}
            </div>
          )}
        </div>
      ) : (
        <div className="p-3 text-[13px] text-neutral-700 leading-relaxed whitespace-pre-wrap break-words">
          {hoverPreview.content}
        </div>
      )}
    </div>
  );
};

HoverPreviewTooltip.displayName = 'HoverPreviewTooltip';
